"use client";

import { ToastType } from "@/types/common";
import { Toggle } from "@monsterindia/global-ui-components";
import { AutoApplyNudge } from "@monsterindia/l1-apop";
import { useSearchParams } from "next/navigation";
import React, { useEffect, useState } from "react";

import L1ApopWrapper from "@/components/common/autoApplyV2/components/l1Apop";

import { AutoApplyData, fetchAutoApply } from "../utils/fetchAutoApply";
import DisclaimerModal from "./DisclaimerModal";
import "./resources/AutoApply.scss";
import Toast from "./utils/Toast";
import { autoApplyPostFunc } from "./utils/autoApplyPostData";
import { handleViewPreferences } from "./utils/handleViewPreferences";

type AutoApplyNewProps = {
  data: AutoApplyData | null;
};
type AutoApplyAction = "renew" | "update" | "enable" | "preferences";

function AutoApplyNew({ data }: AutoApplyNewProps) {
  const [isToggle, setIsToggle] = useState(false);
  const [isOpenModal, setIsOpenModal] = useState(false);
  const [showAutoApplyNudge, setShowAutoApplyNudge] = useState(false);
  const [autoAppyPreferences, setAutoApplyPreference] = useState({
    roles: [],
    locations: [],
    skills: [],
  });

  const searchParams = useSearchParams();
  const autoApplyStatus = searchParams.get("autoApplyStatus");

  useEffect(() => {
    if (
      (autoApplyStatus === "renew" || autoApplyStatus === "enable") &&
      data !== null
    ) {
      handleAutoApplyPrefence();
    }
  }, [autoApplyStatus, data]);

  const [showL1Card, setShowL1Card] = useState(false);
  const [validateProfileData, setValidateProfileData] = useState(null);
  const [autoApplyData, setAutoApplyData] = useState<any>(data);

  const [toast, setToast] = useState<ToastType>({
    show: false,
    type: "general",
    message: "",
  });

  const showToast = (toastData: ToastType) => {
    setToast(toastData);
  };

  const getAutoApplyData = async () => {
    try {
      const data = await fetchAutoApply();

      setAutoApplyData(data);
    } catch (error) {
      console.error("Failed to fetch Auto Apply data:", error);
    } finally {
      // setAutoApplyLoading(false);
    }
  };

  useEffect(() => {
    getAutoApplyData();
  }, []);

  useEffect(() => {
    setIsToggle(autoApplyData?.auto_apply_status?.status === "ACTIVE");
    const autoAppyPreferences = {
      roles: autoApplyData?.jobPreferenceSection?.jobPreference?.roles ?? [],
      locations:
        autoApplyData?.jobPreferenceSection?.jobPreference?.locations ?? [],
      skills:
        autoApplyData?.skillSection.skills.map((skill: any) => {
          return {
            ...skill.skill,
          };
        }) ?? [],
    };
    setAutoApplyPreference(autoAppyPreferences);
  }, [data, autoApplyData]);

  const handleAutoApplyAction = async (data: any) => {
    try {
      const response = await autoApplyPostFunc({ ...data });

      if (!response) {
        throw new Error("Error in Auto Apply API response");
      }

      console.log("autoApply:res", response);

      if (response.error) {
        throw new Error("something Went Wrong");
      } else {
        showToast({
          show: true,
          type: "success",
          message:
            data.action === "enable"
              ? "Auto-apply Enabled successfully"
              : "Auto-apply Disabled successfully",
        });
        setIsToggle(data.action === "enable");
        setIsOpenModal(false);
        setShowAutoApplyNudge(false);
        getAutoApplyData();
      }
    } catch (error) {
      console.error(`Failed to ${data.action} auto apply:`, error);

      showToast({
        show: true,
        type: "error",
        message: "Something went wrong. Please try again.",
      });
    }
  };

  const handleAutoApplyPrefence = async () => {
    try {
      const { data: validateData, handledByApp } =
        await handleViewPreferences();

      if (!validateData) {
        return;
      }

      // App flow
      if (handledByApp) {
        return;
      }

      // Web flow
      if (validateData.validateProfileResponse === 200) {
        setShowAutoApplyNudge(true);
      } else if (validateData.actionType) {
        setShowL1Card(true);
        setValidateProfileData(validateData);
      }
    } catch (error) {
      console.log("Something Went Wrong", error);
    }
  };

  const handleToggleChange = () => {
    if (isToggle) {
      setIsOpenModal(true);
    } else {
      handleAutoApplyPrefence();
    }
  };

  const handleDisable = () => {
    handleAutoApplyAction({ action: "disable" });
  };
  const handleAutoApplySubmit = (data: any) => {
    handleAutoApplyAction(data);
  };
  const closeAutoApplyPreference = () => {
    setShowAutoApplyNudge(false);
  };
  const removeL1Card = () => {
    setShowL1Card(false);
    setShowAutoApplyNudge(true);
  };
  return (
    <div>
      <Toast toast={toast} setToast={setToast} />

      <div className="container">
        <div className="container__tab">
          <div className="container__heading">AI Auto Apply</div>

          <div className="container__toggle">
            <Toggle
              size="small"
              checked={isToggle}
              onChange={handleToggleChange}
              value="auto-apply"
            />
          </div>
        </div>

        <p className="container__content">
          Automatically apply to best-matching jobs as soon as they are posted.
          Keep your profile details updated for the best results.
        </p>

        {isToggle && (
          <button
            type="button"
            className="container__prefereneces"
            onClick={handleAutoApplyPrefence}
          >
            View Preferences
          </button>
        )}
      </div>

      {isOpenModal && (
        <DisclaimerModal
          isOpenModal={isOpenModal}
          setIsOpenModal={setIsOpenModal}
          handleDisable={handleDisable}
        />
      )}
      {showAutoApplyNudge && (
        <AutoApplyNudge
          preferences={autoAppyPreferences}
          // autoApplyStatus={autoApplyData}
          onSubmitHandler={handleAutoApplySubmit}
          showNudge={showAutoApplyNudge}
          closeNudge={closeAutoApplyPreference}
          ctaText={autoApplyStatus ? autoApplyStatus : isToggle ? "update" : ""}
        />
      )}
      {showL1Card && (
        <L1ApopWrapper
          data={validateProfileData}
          removeL1Card={removeL1Card}
          showL1ApopOnEvent={showL1Card}
          eventData={null}
        />
      )}
    </div>
  );
}

export default AutoApplyNew;
